/**
 * OrbitHotspot2D Component
 * Renders a 2D HTML hotspot that follows a 3D position on the satellite orbit
 */

import { useRef, useState, useMemo, useEffect, memo, useSyncExternalStore } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import { Html } from '@react-three/drei'
import * as THREE from 'three'
import { Vector3, SphereGeometry } from 'three'
import { useIframeStore } from '../../../store/iframeStore'
import './Hotspots2D.css'

// Perf #3: ONE shared media-query store for all marker/hotspot instances. Previously each
// component ran 4 separate matchMedia hooks (4 listeners × dozens of instances). Here a
// single module-level store holds 4 MediaQueryLists with one change-listener each, and
// components subscribe via useSyncExternalStore — so the listener count is constant
// regardless of how many hotspots are on screen.
// Tablet Landscape now covers 900–1919 (the old desktop720 1367–1919 tier was
// removed and folded in here), so sub-1080p / windowed desktops get the
// iPad-landscape hotspot sizing instead of a dedicated "desktop standard" size.
const _mqDefs = {
  is4K:             '(min-width: 2560px)',
  isMobilePortrait: '(max-width: 767px) and (orientation: portrait)',
  isTabletLandscape:'(min-width: 900px) and (max-width: 1919px) and (orientation: landscape)',
}
const _mqList = typeof window !== 'undefined'
  ? Object.fromEntries(Object.entries(_mqDefs).map(([k, q]) => [k, window.matchMedia(q)]))
  : null
let _mqSnapshot = _mqList
  ? Object.fromEntries(Object.entries(_mqList).map(([k, m]) => [k, m.matches]))
  : { is4K: false, isMobilePortrait: false, isTabletLandscape: false }
const _mqSubs = new Set()
if (_mqList) {
  const onChange = () => {
    // Recompute the snapshot once (stable object identity only changes when a value flips)
    const next = Object.fromEntries(Object.entries(_mqList).map(([k, m]) => [k, m.matches]))
    const changed = Object.keys(next).some((k) => next[k] !== _mqSnapshot[k])
    if (changed) { _mqSnapshot = next; _mqSubs.forEach((cb) => cb()) }
  }
  Object.values(_mqList).forEach((m) => m.addEventListener('change', onChange))
}
function _mqSubscribe(cb) { _mqSubs.add(cb); return () => _mqSubs.delete(cb) }
function _mqGetSnapshot() { return _mqSnapshot }

// Shared device flags — one subscription per component, zero extra matchMedia listeners.
function useDeviceFlags() {
  return useSyncExternalStore(_mqSubscribe, _mqGetSnapshot, _mqGetSnapshot)
}
function use4K() { return useDeviceFlags().is4K }
function useMobilePortrait() { return useDeviceFlags().isMobilePortrait }
function useTabletLandscape() { return useDeviceFlags().isTabletLandscape }
function useHotspotLineScale() {
  const f = useDeviceFlags()
  // 4K renders at ~2x pixel density, so the line/dot/pulse must be 2x to match
  // the physical size they have at 1080p.
  if (f.is4K) return 2
  // Tablet Landscape: connector lines/dots/pulses/anchor squares doubled (per
  // user) from the old 0.5 → 1.0.
  return f.isMobilePortrait ? 0.33 : f.isTabletLandscape ? 1 : 1
}

// Shared pulse ring SVG — absolutely centred on the dot, real size so it's never
// clipped. `scale` multiplies the ring size (tablet doubles it, per user).
function PulseRing({ color, scale = 1 }) {
  return (
    <svg
      width="80"
      height="80"
      viewBox="-40 -40 80 80"
      style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: `translate(-50%, -50%) scale(${scale})`,
        overflow: 'visible',
        pointerEvents: 'none',
      }}
    >
      <circle cx="0" cy="0" r="4" fill="none" stroke={color} style={{ strokeWidth: 'var(--pulse-stroke-width, 1.5)' }} opacity="0">
        <animate attributeName="r"       values="4;36;36" dur="1.5s" repeatCount="indefinite" />
        <animate attributeName="opacity" values="0.8;0;0" dur="1.5s" repeatCount="indefinite" />
      </circle>
    </svg>
  )
}

// Dot with pulse — solid red dot (opacity always 1) + animated ring behind it.
// `pulseScale` sizes the ring (tablet = 2, per user); the solid dot uses `size`.
function PulseDot({ size, color, pulseScale = 1 }) {
  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <PulseRing color={color} scale={pulseScale} />
      <div
        style={{
          width: size,
          height: size,
          borderRadius: '50%',
          backgroundColor: color,
          boxShadow: `0 0 8px ${color}`,
          position: 'relative',
        }}
      />
    </div>
  )
}

// 3D marker component with HTML overlay - renders inside Canvas
export function OrbitMarker({ 
  orbitRadius, 
  angle, 
  rotationX, 
  rotationZ, 
  dotColor = '#EE3A17',
  bracketColor = '#E3E3E3',
  blink = 1,
  text = '',
  textPosition = 'right',
  textColor = '#E3E3E3',
  onClick = null
}) {
  const groupRef = useRef()
  const is4K = use4K()
  const isMobilePortrait = useMobilePortrait()
  const isTabletLandscape = useTabletLandscape()
  // Tablet Landscape: bracket/dot/pulse doubled (per user) — fs 6→12, dot/gap
  // 3→6. textOffset scaled with them so the label stays clear of the bigger dot.
  const fs = is4K ? '24px' : isTabletLandscape ? '12px' : '16px'
  const dot = is4K ? '12px' : isTabletLandscape ? '6px' : '8px'
  const gap = is4K ? '12px' : isTabletLandscape ? '6px' : '8px'
  const textOffset = is4K ? '42px' : isTabletLandscape ? '21px' : '28px'

  // Calculate 3D position on the TILTED orbit ring.
  // Html renders at the group's world origin, so we must apply rotationX/rotationZ
  // to the XZ-plane point directly — the group rotation alone does nothing for Html.
  const position = useMemo(() => {
    const x0 = Math.cos(angle) * orbitRadius
    const z0 = Math.sin(angle) * orbitRadius
    // Apply rotationX around X axis (y0 = 0 so terms drop out)
    const cosRX = Math.cos(rotationX)
    const sinRX = Math.sin(rotationX)
    const y1 = -z0 * sinRX
    const z1 = z0 * cosRX
    // Apply rotationZ around Z axis
    const cosRZ = Math.cos(rotationZ)
    const sinRZ = Math.sin(rotationZ)
    const x2 = x0 * cosRZ - y1 * sinRZ
    const y2 = x0 * sinRZ + y1 * cosRZ
    return new Vector3(x2, y2, z1)
  }, [angle, orbitRadius, rotationX, rotationZ])
  
  return (
    <group ref={groupRef} position={position}>
      <Html
        sprite
        zIndexRange={[0, 0]}
        style={{
          pointerEvents: 'auto',
          userSelect: 'none'
        }}
      >
        <div
          className="hotspot-3d-marker"
          onClick={onClick}
          style={{ position: 'relative', cursor: onClick ? 'pointer' : 'default' }}
        >
          {/* Brackets + dot centered on the marker point — kept OUTSIDE the text
              pill (the bordered box wraps only the text label). */}
          <div
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              transform: 'translate(-50%, -50%)',
              display: 'flex',
              alignItems: 'center',
              gap: gap,
              fontFamily: 'monospace',
              fontSize: fs,
              fontWeight: 'bold',
              lineHeight: '1'
            }}
          >
            <span className="hotspot-static-label-bracket" style={{ lineHeight: '1' }}>[</span>
            <PulseDot size={dot} color={dotColor} pulseScale={isTabletLandscape ? 2 : 1} />
            <span className="hotspot-static-label-bracket" style={{ lineHeight: '1' }}>]</span>
          </div>
          {/* Text in its own bordered pill, offset to the side of the brackets. */}
          {text && !isMobilePortrait && textPosition === 'left' && (
            <span
              className="hotspot-static-label hotspot-static-label-text"
              style={{ right: textOffset, transform: 'translate(0, -50%)' }}
            >{text}</span>
          )}
          {text && !isMobilePortrait && textPosition === 'right' && (
            <span
              className="hotspot-static-label hotspot-static-label-text"
              style={{ left: textOffset, transform: 'translate(0, -50%)' }}
            >{text}</span>
          )}
        </div>
      </Html>
    </group>
  )
}

// 2D HTML overlay component - renders outside Canvas
export function OrbitHotspot2DOverlay({ 
  screenPos, 
  dotColor, 
  bracketColor, 
  blink, 
  text = '', 
  textPosition = 'right', // 'left' or 'right'
  textColor = '#E3E3E3'
}) {
  // Don't render if not visible or position hasn't been initialized yet
  if (!screenPos.visible || screenPos.x <= 0 || screenPos.y <= 0) return null
  
  const hotspotContent = (
    <>
      <span className="hotspot-bracket" style={{ color: bracketColor }}>[</span>
      <PulseDot size="8px" color={dotColor} />
      <span className="hotspot-bracket" style={{ color: bracketColor }}>]</span>
    </>
  )
  
  return (
    <div
      className="orbit-hotspot-2d"
      style={{
        position: 'fixed',
        left: `${screenPos.x}px`,
        top: `${screenPos.y}px`,
        zIndex: 1000,
        fontFamily: 'monospace',
        fontSize: '16px',
        fontWeight: 'bold'
      }}
    >
      {/* Hotspot centered at exact position */}
      <div style={{
        position: 'absolute',
        left: 0,
        top: 0,
        transform: 'translate(-50%, -50%)',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }}>
        {hotspotContent}
      </div>
      
      {/* Text positioned absolutely relative to hotspot */}
      {text && textPosition === 'left' && (
        <span className="hotspot-text" style={{ 
          position: 'absolute',
          right: '28px',
          top: '50%',
          transform: 'translate(0, -50%)',
          color: textColor,
          whiteSpace: 'nowrap'
        }}>{text}</span>
      )}
      {text && textPosition === 'right' && (
        <span className="hotspot-text" style={{ 
          position: 'absolute',
          left: '28px',
          top: '50%',
          transform: 'translate(0, -50%)',
          color: textColor,
          whiteSpace: 'nowrap'
        }}>{text}</span>
      )}
    </div>
  )
}

// Static position marker with HTML overlay - for hotspots at fixed 3D positions
export function StaticMarker({ 
  position,
  dotColor = '#EE3A17',
  bracketColor = '#E3E3E3',
  blink = 1,
  text = '',
  textPosition = 'right',
  textColor = '#E3E3E3',
  onClick
}) {
  const is4K = use4K()
  const isMobilePortrait = useMobilePortrait()
  const isTabletLandscape = useTabletLandscape()
  // Tablet Landscape: bracket/dot/pulse doubled (per user, EXPLORE SLS/ORION) —
  // fs 6→12, dot/gap 3→6, textOffset scaled 10.5→21 to clear the bigger dot.
  const fs = is4K ? '24px' : isTabletLandscape ? '12px' : '16px'
  const dot = is4K ? '12px' : isTabletLandscape ? '6px' : '8px'
  const gap = is4K ? '12px' : isTabletLandscape ? '6px' : '8px'
  const textOffset = is4K ? '42px' : isTabletLandscape ? '21px' : '28px'
return (
    <group position={position}>
      <Html
        sprite
        zIndexRange={[0, 0]}
        style={{
          pointerEvents: onClick ? 'auto' : 'none',
          userSelect: 'none'
        }}
      >
        <div
          className="hotspot-3d-marker"
          onClick={onClick}
          style={{
            position: 'relative',
            cursor: onClick ? 'pointer' : 'default'
          }}
        >
          {/* Brackets + dot centered on the marker point — kept OUTSIDE the text
              pill (the bordered box wraps only the text label). */}
          <div
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              transform: 'translate(-50%, -50%)',
              display: 'flex',
              alignItems: 'center',
              gap: gap,
              fontFamily: 'monospace',
              fontSize: fs,
              fontWeight: 'bold',
              lineHeight: '1'
            }}
          >
            <span className="hotspot-static-label-bracket" style={{ lineHeight: '1' }}>[</span>
            <PulseDot size={dot} color={dotColor} pulseScale={isTabletLandscape ? 2 : 1} />
            <span className="hotspot-static-label-bracket" style={{ lineHeight: '1' }}>]</span>
          </div>
          {/* Text in its own bordered pill, offset to the side of the brackets. */}
          {text && textPosition === 'left' && (
            <span
              className="hotspot-static-label hotspot-static-label-text"
              style={{ right: textOffset, transform: 'translate(0, -50%)' }}
            >{text}</span>
          )}
          {text && textPosition === 'right' && (
            <span
              className="hotspot-static-label hotspot-static-label-text"
              style={{ left: textOffset, transform: 'translate(0, -50%)' }}
            >{text}</span>
          )}
        </div>
      </Html>
    </group>
  )
}

// Earth surface marker with HTML overlay - for hotspots at specific lat/long on rotating Earth.
// Uses the Earth's actual inner group world matrix to compute position, eliminating
// the need to replicate Earth's transform chain (tilt + rotation) independently.
// This prevents desync after render-loop pause/resume (iframe display).
export const EarthSurfaceMarker = memo(function EarthSurfaceMarker({ 
  latitude, 
  longitude, 
  latitudeOffset = 0, 
  longitudeOffset = 0, 
  earthRadius = 1, 
  earthInnerGroupRef,
  dotColor = '#EE3A17',
  bracketColor = '#E3E3E3',
  blink = 1,
  text = '',
  textPosition = 'right',
  textColor = '#E3E3E3',
  onClick = null,
  variant = 'brackets'
}) {
  const groupRef = useRef()
  const is4K = use4K()
  const isMobilePortrait = useMobilePortrait()
  const isTabletLandscape = useTabletLandscape()
  // Tablet Landscape: connector geometry doubled (per user) — dot/gap 3→6,
  // svgScale (leader line + pulse + anchor) 0.375→0.75, textOffset 10.5→21 to
  // clear the bigger dot. fs (the connected label text) left as-is.
  const fs = is4K ? '24px' : isMobilePortrait ? '11px' : isTabletLandscape ? '6px' : '16px'
  const dot = is4K ? '12px' : isTabletLandscape ? '6px' : '8px'
  const gap = is4K ? '12px' : isTabletLandscape ? '6px' : '8px'
  const textOffset = is4K ? '42px' : isTabletLandscape ? '21px' : '28px'
  const svgScale = is4K ? 1.5 : isTabletLandscape ? 0.75 : 1

  // Hide the HTML overlay when an iframe is open — keeps useFrame updating
  // so when the iframe closes the marker is already at the correct position.
  const markersVisible = useIframeStore(state => state.markersVisible)

  // Reusable vectors to avoid allocations every frame
  const _localPos = useMemo(() => new Vector3(), [])
  const _worldPos = useMemo(() => new Vector3(), [])

  // Priority -1 ensures this runs BEFORE Drei's <Html> useFrame (priority 0),
  // so the world position is up-to-date when Drei samples it for projection.
  useFrame(() => {
    if (!groupRef.current || !earthInnerGroupRef?.current) return

    // Convert lat/long (with offsets) to local-space position on sphere
    const lat = (latitude + latitudeOffset) * (Math.PI / 180)
    const lon = (longitude + longitudeOffset) * (Math.PI / 180)

    _localPos.set(
      earthRadius * Math.cos(lat) * Math.cos(lon),
      earthRadius * Math.sin(lat),
      earthRadius * Math.cos(lat) * Math.sin(lon)
    )

    // Transform through Earth's world matrix (includes tilt + spin rotation)
    earthInnerGroupRef.current.updateWorldMatrix(true, false)
    _worldPos.copy(_localPos).applyMatrix4(earthInnerGroupRef.current.matrixWorld)

    groupRef.current.position.copy(_worldPos)
  }, -1)

  return (
    <group ref={groupRef}>
      <Html
        sprite
        zIndexRange={[0, 0]}
        style={{
          pointerEvents: markersVisible ? 'auto' : 'none',
          userSelect: 'none'
        }}
      >
        <div className="hotspot-3d-marker" style={{ position: 'relative', cursor: onClick ? 'pointer' : 'default', display: markersVisible ? undefined : 'none' }} onClick={onClick}>
          {variant === 'line' ? (
            /* Right-aligned: text ────╱─■ where ■ is at the anchor point */
            <div
              style={{
                position: 'absolute',
                left: 0,
                top: 0,
                transform: 'translate(-100%, -50%)',
                display: 'flex',
                alignItems: 'center',
                lineHeight: '1'
              }}
            >
              {/* Text */}
              {text && (
                <span style={{
                  marginRight: '10px',
                  color: dotColor,
                  fontFamily: 'monospace',
                  fontSize: fs,
                  fontWeight: 'bold',
                  letterSpacing: '1.5px',
                  whiteSpace: 'nowrap',
                  lineHeight: '1',
                  textTransform: 'uppercase',
                }}>{text}</span>
              )}
              {/* SVG: long horizontal → diagonal up → short horizontal → square with ping */}
              <svg
                width={80 * svgScale}
                height={14 * svgScale}
                viewBox="0 0 80 14"
                style={{ flexShrink: 0, display: 'block', overflow: 'visible' }}
              >
                <polyline
                  points="0,7 38,7 52,3 73,3"
                  stroke={dotColor}
                  strokeWidth="1"
                  fill="none"
                  strokeLinejoin="miter"
                />
                <circle cx="76.5" cy="3.5" r="3" fill="none" stroke={dotColor} style={{ strokeWidth: 'var(--pulse-stroke-width, 1.5)' }} opacity="0">
                  <animate attributeName="r" values="3;36;36" dur="1.5s" repeatCount="indefinite" />
                  <animate attributeName="opacity" values="0.8;0;0" dur="1.5s" repeatCount="indefinite" />
                </circle>
                <rect x="73" y="0" width="7" height="7" fill={dotColor} style={{ filter: `drop-shadow(0 0 4px ${dotColor})` }} />
              </svg>
            </div>
          ) : (
            <>
              {/* Brackets + dot centered on the marker point — kept OUTSIDE the
                  text pill (the bordered box wraps only the text label). */}
              <div
                style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  transform: 'translate(-50%, -50%)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: gap,
                  fontFamily: 'monospace',
                  fontSize: fs,
                  fontWeight: 'bold',
                  lineHeight: '1'
                }}
              >
                <span className="hotspot-static-label-bracket" style={{ lineHeight: '1' }}>[</span>
                <PulseDot size={dot} color={dotColor} pulseScale={isTabletLandscape ? 2 : 1} />
                <span className="hotspot-static-label-bracket" style={{ lineHeight: '1' }}>]</span>
              </div>
              {/* Text in its own bordered pill, offset to the side of the brackets. */}
              {text && !isMobilePortrait && textPosition === 'left' && (
                <span
                  className="hotspot-static-label hotspot-static-label-text"
                  style={{ right: textOffset, transform: 'translate(0, -50%)' }}
                >{text}</span>
              )}
              {text && !isMobilePortrait && textPosition === 'right' && (
                <span
                  className="hotspot-static-label hotspot-static-label-text"
                  style={{ left: textOffset, transform: 'translate(0, -50%)' }}
                >{text}</span>
              )}
            </>
          )}
        </div>
      </Html>
    </group>
  )
})

// Moon surface marker with HTML overlay - for hotspots at specific lat/long on static Moon
export function MoonSurfaceMarker({ 
  latitude, 
  longitude, 
  moonRadius = 0.183, 
  moonTilt = 1.54,
  dotColor = '#EE3A17',
  bracketColor = '#E3E3E3',
  blink = 1,
  text = '',
  textPosition = 'right',
  textColor = '#E3E3E3',
  onClick = null,
  variant = 'brackets'
}) {
  const markerRef = useRef()
  const outerGroupRef = useRef()
  const is4K = use4K()
  const isMobilePortrait = useMobilePortrait()
  const isTabletLandscape = useTabletLandscape()
  // Tablet Landscape: connector geometry doubled (per user) — dot/gap 3→6,
  // svgScale (leader line + pulse + anchor) 0.375→0.75, textOffset 10.5→21 to
  // clear the bigger dot. fs (the connected label text) left as-is.
  const fs = is4K ? '24px' : isMobilePortrait ? '11px' : isTabletLandscape ? '6px' : '16px'
  const dot = is4K ? '12px' : isTabletLandscape ? '6px' : '8px'
  const gap = is4K ? '12px' : isTabletLandscape ? '6px' : '8px'
  const textOffset = is4K ? '42px' : isTabletLandscape ? '21px' : '28px'
  const svgScale = is4K ? 1.5 : isTabletLandscape ? 0.75 : 1
  
  // Calculate position once on mount - Moon is static
  const position = useMemo(() => {
    const lat = latitude * (Math.PI / 180)
    const lon = longitude * (Math.PI / 180)
    
    const x = moonRadius * Math.cos(lat) * Math.cos(lon)
    const y = moonRadius * Math.sin(lat)
    const z = moonRadius * Math.cos(lat) * Math.sin(lon)
    
    return [x, y, z]
  }, [latitude, longitude, moonRadius])
  
  // Apply Moon's tilt rotation to outer group
  const tiltRotationRadians = moonTilt * (Math.PI / 180)
  
  return (
    <group ref={outerGroupRef} rotation={[0, 0, tiltRotationRadians]}>
      <group ref={markerRef} position={position}>
        <Html
          sprite
          zIndexRange={[0, 0]}
          style={{
            pointerEvents: onClick ? 'auto' : 'none',
            userSelect: 'none'
          }}
        >
          <div className="hotspot-3d-marker" style={{ position: 'relative', cursor: onClick ? 'pointer' : 'default' }} onClick={onClick}>
            {variant === 'line' ? (
              <div style={{ position: 'absolute', left: 0, top: 0, transform: 'translate(-100%, -50%)', display: 'flex', alignItems: 'center', lineHeight: '1', pointerEvents: onClick ? 'auto' : 'none' }}>
                {text && (
                  <span style={{ color: textColor, fontSize: fs, fontFamily: "'Public Sans', sans-serif", whiteSpace: 'nowrap', marginRight: '10px', fontWeight: 400, letterSpacing: '3px', textTransform: 'uppercase' }}>{text}</span>
                )}
                <svg width={80 * svgScale} height={14 * svgScale} viewBox="0 0 80 14" style={{ flexShrink: 0, display: 'block', overflow: 'visible' }}>
                  <polyline points="0,7 38,7 52,3 73,3" stroke={dotColor} strokeWidth="1" fill="none" strokeLinejoin="miter" />
                  <circle cx="76.5" cy="3.5" r="3" fill="none" stroke={dotColor} style={{ strokeWidth: 'var(--pulse-stroke-width, 1.5)' }} opacity="0">
                    <animate attributeName="r" values="3;36;36" dur="1.5s" repeatCount="indefinite" />
                    <animate attributeName="opacity" values="0.8;0;0" dur="1.5s" repeatCount="indefinite" />
                  </circle>
                  <rect x="73" y="0" width="7" height="7" fill={dotColor} style={{ filter: `drop-shadow(0 0 4px ${dotColor})` }} />
                </svg>
              </div>
            ) : (
              <>
                {/* Brackets + dot centered on the marker point — kept OUTSIDE the
                    text pill (the bordered box wraps only the text label). */}
                <div style={{ position: 'absolute', left: 0, top: 0, transform: 'translate(-50%, -50%)', display: 'flex', alignItems: 'center', gap: gap, fontFamily: 'monospace', fontSize: fs, fontWeight: 'bold', lineHeight: '1' }}>
                  <span className="hotspot-static-label-bracket" style={{ lineHeight: '1' }}>[</span>
                  <PulseDot size={dot} color={dotColor} pulseScale={isTabletLandscape ? 2 : 1} />
                  <span className="hotspot-static-label-bracket" style={{ lineHeight: '1' }}>]</span>
                </div>
                {/* Text in its own bordered pill, offset to the side of the brackets. */}
                {text && !isMobilePortrait && textPosition === 'left' && (
                  <span className="hotspot-static-label hotspot-static-label-text" style={{ right: textOffset, transform: 'translate(0, -50%)' }}>{text}</span>
                )}
                {text && !isMobilePortrait && textPosition === 'right' && (
                  <span className="hotspot-static-label hotspot-static-label-text" style={{ left: textOffset, transform: 'translate(0, -50%)' }}>{text}</span>
                )}
              </>
            )}
          </div>
        </Html>
      </group>
    </group>
  )
}

// Artemis tracker - for tracking the Artemis rocket position
export function ArtemisTracker({ onPositionUpdate }) {
  const { camera, size } = useThree()
  
  // Reusable Vector3 objects to avoid creating new objects every frame
  const worldPosRef = useRef(new Vector3())
  const screenPosRef = useRef(new Vector3())
  
  // Reusable position update object to avoid creating new objects every frame
  const posUpdateRef = useRef({ x: 0, y: 0, z: 0, visible: false })
  
  // Track last updated position to prevent unnecessary state updates
  const lastPosRef = useRef({ x: -1, y: -1, z: 0, visible: false })
  
  useFrame(() => {
    // Access the Artemis group from global window reference
    if (window.artemisGroupRef) {
      // Get world position of the Artemis group - reuse Vector3
      window.artemisGroupRef.getWorldPosition(worldPosRef.current)
      
      // Project 3D position to 2D screen coordinates - reuse Vector3
      screenPosRef.current.copy(worldPosRef.current).project(camera)
      
      // Convert to pixel coordinates - reuse object to prevent GC churn
      posUpdateRef.current.x = (screenPosRef.current.x * 0.5 + 0.5) * size.width
      posUpdateRef.current.y = (-(screenPosRef.current.y * 0.5) + 0.5) * size.height
      posUpdateRef.current.z = screenPosRef.current.z
      posUpdateRef.current.visible = screenPosRef.current.z < 1 && screenPosRef.current.z > -1
      
      // Only update if position changed significantly (threshold: 0.5 pixels)
      // This prevents jittery re-renders from sub-pixel movements
      const dx = Math.abs(posUpdateRef.current.x - lastPosRef.current.x)
      const dy = Math.abs(posUpdateRef.current.y - lastPosRef.current.y)
      const visibilityChanged = posUpdateRef.current.visible !== lastPosRef.current.visible
      
      if (dx > 0.5 || dy > 0.5 || visibilityChanged) {
        lastPosRef.current.x = posUpdateRef.current.x
        lastPosRef.current.y = posUpdateRef.current.y
        lastPosRef.current.z = posUpdateRef.current.z
        lastPosRef.current.visible = posUpdateRef.current.visible
        
        onPositionUpdate(posUpdateRef.current)
      }
    }
  })
  
  return null // This component doesn't render anything in 3D
}

// Artemis 2D overlay - renders square brackets around Artemis
export function Artemis2DOverlay({ 
  screenPos, 
  bracketColor = '#EE3A17',
  visible = true,
  blink = 1
}) {
  // Don't render if not visible or position hasn't been initialized yet
  if (!visible || !screenPos.visible || screenPos.x <= 0 || screenPos.y <= 0) return null
  
  return (
    <div
      style={{
        position: 'fixed',
        left: `${screenPos.x}px`,
        top: `${screenPos.y}px`,
        zIndex: 1000,
        pointerEvents: 'none'
      }}
    >
      {/* Brackets centered at Artemis position */}
      <div style={{
        position: 'absolute',
        left: 0,
        top: 0,
        transform: 'translate(-50%, -50%)',
        display: 'flex',
        alignItems: 'center',
        gap: '24px',
        fontFamily: 'monospace',
        fontSize: '24px',
        fontWeight: 'bold',
        color: bracketColor,
        opacity: blink
      }}>
        <span>[</span>
        <span>]</span>
      </div>
    </div>
  )
}

export default OrbitMarker

/**
 * SatelliteHotspot
 *
 * A 3-segment callout line anchored at a satellite's 3D world center.
 * All geometry is configurable via the `line` prop (see locations.json).
 *
 * Props:
 *   satelliteGroupRef — ref to the satellite Three.js Group
 *   offset            — [x, y, z] world-space offset from group center
 *   direction         — "left" | "right"
 *   line              — geometry config object (all values in SVG/CSS pixels):
 *     seg1      — horizontal run from anchor square to start of diagonal
 *     seg2      — horizontal width of the diagonal segment
 *     diagDrop  — vertical rise/drop of the diagonal (positive = drops downward)
 *     seg3      — horizontal run from end of diagonal to text
 *   text              — label string
 *   color             — stroke/fill/text color (default brand red #EE3A17)
 *   visible           — show/hide without unmounting
 */
// Perf #5: memoised so it only re-renders when its own props change (the parent app
// re-renders frequently on stage 0–3 state churn). Call sites must pass stable props
// (shared constant offsets + useCallback'd onClick) for this to take effect.
export const SatelliteHotspot = memo(function SatelliteHotspot({
  satelliteGroupRef,
  offset = [0, 0, 0],
  mobileOffset = null,
  direction = 'right',
  line = {},
  text = '',
  color = '#EE3A17',
  visible = true,
  dotOnly = false,
  flipDiagOnMobile = true,
  noMobileDrop = false,
  clickableArrow = true,
  // Non-interactive label (e.g. SLS attachment hotspots — no click handler):
  // renders without the red fill/border, on a plain black background instead.
  nonInteractive = false,
  onClick = null,
  // Merged hotspots (e.g. Attitude Control, Propulsion System): extra blinking dots that
  // share this hotspot's single label/line. Each tracks its own 3D anchor, projected to
  // screen each frame. Pass an array of refs (one per extra dot).
  secondaryGroupRefs = null,
}) {
  const groupRef = useRef()
  const isMobilePortrait = useMobilePortrait()
  const { camera, size } = useThree()
  // One DOM-ref pair (rect + pulse) per secondary dot, kept stable across renders.
  const secRefs = useRef([])
  const secList = Array.isArray(secondaryGroupRefs) ? secondaryGroupRefs : (secondaryGroupRefs ? [secondaryGroupRefs] : [])

  // Use mobileOffset on mobile portrait if provided, otherwise fall back to offset
  const activeOffset = isMobilePortrait && mobileOffset ? mobileOffset : offset

  // Geometry — each value is independently named for easy JSON editing.
  // Mobile portrait: thicken the marks (anchor square, terminal dot, line stroke,
  // pulse) 3× so they read at phone size — but NOT the leader-line LENGTH
  // (seg1/seg2/seg3 stay on lineScale) so labels don't get pushed off-screen.
  const lineScale = useHotspotLineScale()
  const dotBoost  = isMobilePortrait ? 3 : 1
  // Mobile portrait: labels drop below the anchor by default (straight vertical
  // drop using the combined segment length). When noMobileDrop is true the
  // hotspot keeps its standard leader line (off to the side, like tablet).
  const verticalDrop = isMobilePortrait && !noMobileDrop
  const seg1     = verticalDrop ? 0 : (line.seg1 ?? 38) * lineScale
  const seg2     = verticalDrop ? 0 : (line.seg2 ?? 14) * lineScale
  const seg3     = verticalDrop ? 0 : (line.seg3 ?? 21) * lineScale
  const diagDrop = verticalDrop
    ? (((line.seg1 ?? 38) + (line.seg2 ?? 14) + (line.seg3 ?? 21)) * lineScale)
    : (line.diagDrop ?? 4) * lineScale * (isMobilePortrait && flipDiagOnMobile ? -1 : 1)

  // SVG coordinate system — anchor square is always on the LEFT end.
  // For direction="left" we mirror the SVG with scaleX(-1) so the line runs left.
  const squareSize = 7 * lineScale * dotBoost   // anchor square
  const halfSq     = squareSize / 2
  const textGap    = 8 * lineScale   // px gap between line end and text
  const dotRadius  = 2 * lineScale * dotBoost   // terminal dot
  const strokeW    = 1 * lineScale * dotBoost   // line + pulse stroke base
  const pulseMax   = 24 * lineScale * dotBoost  // pulse ring expansion radius

  // Y positions — line runs at the square's vertical centre, diagonal drops below
  const lineY   = halfSq                       // y of the main horizontal (top of square = 0)
  const diagEndY = lineY + diagDrop             // y at end of diagonal

  // X positions of each node along the polyline
  const x0 = halfSq                            // anchor square centre x
  const x1 = x0 + seg1                         // end of seg1
  const x2 = x1 + seg2                         // end of seg2 (diagonal)
  const x3 = x2 + seg3                         // end of seg3 (text start)


  // SVG canvas size — add right-side padding so the pulse ring is never clipped
  const svgW = x3 + textGap
  const svgH = Math.max(squareSize, lineY + Math.abs(diagDrop) + halfSq + 1)

  // Track satellite group world position each frame
  const _worldPos  = useMemo(() => new Vector3(), [])
  const _offsetVec = useMemo(
    () => new Vector3(activeOffset[0], activeOffset[1], activeOffset[2]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeOffset[0], activeOffset[1], activeOffset[2]]
  )

  useFrame(() => {
    if (!groupRef.current || !satelliteGroupRef?.current) return
    satelliteGroupRef.current.getWorldPosition(_worldPos)
    _worldPos.add(_offsetVec)
    groupRef.current.position.copy(_worldPos)
  }, -1)

  // Approach B — each secondary dot tracks its own 3D anchor (e.g. Attitude Control's
  // pitch thrusters, or Propulsion System's man/aux thrusters). Each frame we project the
  // primary anchor + every secondary anchor to screen pixels and write each secondary's
  // offset (relative to the primary, in this SVG's coordinate space) straight onto its
  // rect/pulse — imperative so it never triggers a React re-render. The single leader
  // line stays on the primary anchor.
  const _secWorld  = useMemo(() => new Vector3(), [])
  const _priScreen = useMemo(() => new Vector3(), [])
  const _secScreen = useMemo(() => new Vector3(), [])
  const isLeft = direction === 'left'
  useFrame(() => {
    if (!satelliteGroupRef?.current || !secList.length) return
    // Primary anchor world point (mirrors the tracking useFrame above)
    satelliteGroupRef.current.getWorldPosition(_priScreen).add(_offsetVec)
    _priScreen.project(camera)
    for (let i = 0; i < secList.length; i++) {
      const grp = secList[i]?.current
      const dom = secRefs.current[i]
      if (!grp || !dom?.rect) continue
      grp.getWorldPosition(_secWorld)
      _secScreen.copy(_secWorld).project(camera)
      const dxPx = ((_secScreen.x - _priScreen.x) * 0.5) * size.width
      const dyPx = (-(_secScreen.y - _priScreen.y) * 0.5) * size.height
      // In a left-facing hotspot the SVG is mirrored (scaleX(-1)), so flip the x
      // delta to keep the dot on the correct side after mirroring. In vertical mode
      // (mobile portrait) the mirror is OFF (see the svg transform), so the delta
      // must NOT be flipped — otherwise the secondary dots land on the wrong side,
      // floating away from their anchors.
      const mirrored = isLeft && !verticalDrop
      const nx = x0 + (mirrored ? -dxPx : dxPx)
      const ny = lineY + dyPx
      dom.rect.setAttribute('x', nx - halfSq)
      dom.rect.setAttribute('y', ny - halfSq)
      if (dom.pulse) {
        dom.pulse.setAttribute('cx', nx)
        dom.pulse.setAttribute('cy', ny)
      }
    }
  })

  // Container transform — anchor square centre sits at the Html origin (0,0).
  // For "right": shift left by x0 so the square is at origin, line extends right.
  // For "left":  SVG is mirrored (scaleX(-1)), so visually the square is on the
  //              right end of the flipped drawing. We still shift left by x0 so
  //              that right edge (the square) sits at origin.
  // Vertical mode: no horizontal mirror — the line goes straight down at x0, so
  // the anchor square sits at the origin regardless of the JSON direction.
  const translateX = verticalDrop ? -x0 : (isLeft ? -(svgW - x0) : -x0)
  const translateY = -lineY

  return (
    <group ref={groupRef}>
      {/* Perf #2: mount the drei <Html> only when visible — a hidden <Html> still runs
          its per-frame projection useFrame. The outer <group> keeps tracking the model
          group's world position regardless, so the Html re-mounts at the correct spot. */}
      {visible && (
      <Html zIndexRange={[0, 0]} style={{ pointerEvents: 'auto', userSelect: 'none' }}>
        <div style={{ display: 'block', transform: `translate(${translateX}px, ${translateY}px)` }}>
          <div
            className="satellite-hotspot"
            style={{
              cursor: onClick && !dotOnly ? 'pointer' : 'default',
              pointerEvents: onClick && !dotOnly ? 'auto' : 'none',
            }}
            onClick={dotOnly ? undefined : onClick}
          >
            <svg
              width={svgW}
              height={svgH}
              viewBox={`0 0 ${svgW} ${svgH}`}
              style={{
                display: 'block',
                overflow: 'visible',
                transform: (isLeft && !verticalDrop) ? 'scaleX(-1)' : 'none',
              }}
            >
              {/* Pulse ring at anchor */}
              <circle cx={x0} cy={lineY} r={halfSq}
                fill="none" stroke={color}
                strokeWidth={strokeW * 1.5}
                opacity="0"
              >
                <animate attributeName="r"       values={`${halfSq};${pulseMax};${pulseMax}`} dur="1.5s" repeatCount="indefinite" />
                <animate attributeName="opacity" values="0.8;0;0"           dur="1.5s" repeatCount="indefinite" />
              </circle>
              {/* Anchor square */}
              <rect x={0} y={0} width={squareSize} height={squareSize}
                fill={color} style={{ filter: `drop-shadow(0 0 4px ${color})` }}
              />
              {/* Secondary dots (merged hotspots) — square + pulse only, no leader line of
                  their own. Each one's position is driven every frame by the projection
                  useFrame above (tracks its secondaryGroupRefs entry). Initial coords are
                  the primary anchor; the first frame moves them into place. */}
              {secList.map((_, i) => (
                <g key={i}>
                  <circle
                    ref={(el) => { secRefs.current[i] = { ...(secRefs.current[i] || {}), pulse: el } }}
                    cx={x0} cy={lineY} r={halfSq}
                    fill="none" stroke={color} strokeWidth={strokeW * 1.5} opacity="0">
                    <animate attributeName="r"       values={`${halfSq};${pulseMax};${pulseMax}`} dur="1.5s" repeatCount="indefinite" />
                    <animate attributeName="opacity" values="0.8;0;0"           dur="1.5s" repeatCount="indefinite" />
                  </circle>
                  <rect
                    ref={(el) => { secRefs.current[i] = { ...(secRefs.current[i] || {}), rect: el } }}
                    x={x0 - halfSq} y={lineY - halfSq} width={squareSize} height={squareSize}
                    fill={color} style={{ filter: `drop-shadow(0 0 4px ${color})` }} />
                </g>
              ))}
              {/* Callout line + terminal dot — hidden in dotOnly mode (square + pulse only) */}
              {!dotOnly && (
                <>
                  {/* 3-segment polyline: horizontal → diagonal → horizontal */}
                  <polyline
                    points={`${x0},${lineY} ${x1},${lineY} ${x2},${diagEndY} ${x3},${diagEndY}`}
                    stroke={color} strokeWidth={strokeW} fill="none" strokeLinejoin="miter"
                  />
                  {/* Terminal dot */}
                  <circle cx={x3} cy={diagEndY} r={dotRadius} fill={color} />
                </>
              )}
            </svg>

            {/* Text — positioned absolutely so it aligns exactly to the line's far end */}
            {text && !dotOnly && (
              <span
                className={`satellite-hotspot-text${onClick && clickableArrow ? ' satellite-hotspot-text--clickable' : ''}${nonInteractive ? ' satellite-hotspot-text--static' : ''}`}
                style={verticalDrop
                  ? {
                      left: `${translateX + x0}px`,
                      top: `${lineY + diagDrop + textGap}px`,
                      transform: 'translateX(-50%)',
                      color,
                    }
                  : {
                      // For "right": text sits to the right of the SVG end point.
                      // For "left":  SVG is mirrored, so x3 is now visually on the left —
                      //              place text to the left of the container's left edge.
                      ...(isLeft
                        ? { right: svgW - x0 + textGap }
                        : { left:  svgW - x0 + textGap - translateX }),
                      // Vertically centre on the far end of the line
                      top: `${lineY + diagDrop}px`,
                      color,
                    }}
              >{text}</span>
            )}
          </div>
        </div>
      </Html>
      )}
    </group>
  )
})
